import React, { useState, useRef } from "react";
import { Mic, MicOff, Phone, Video, VideoOff, Monitor, Share2, ChevronDown, ShareIcon, ScreenShareOff, ScreenShare, ScreenShareIcon } from "lucide-react";
import "./ControlBarOption.scss";

const ControlBar = ({
  mediaState,
  onToggleVideo,
  onToggleAudio,
  onLeave,
  onEndConsultation,
  onToggleScreenShare,
  canShareScreen = true,
  canLeave = true,
  role = "Patient",
  mediaStream,
  zmClient,
  isAudioDisabled=false,
}) => {
  const [showAudioDevices, setShowAudioDevices] = useState(false);
  const [showVideoDevices, setShowVideoDevices] = useState(false);
  const [micList, setMicList] = useState([]);
  const [cameraList, setCameraList] = useState([]);
  const [activeMicrophone, setActiveMicrophone] = useState('');
  const [activeCamera, setActiveCamera] = useState('');

  const audioRef = useRef(null);
  const videoRef = useRef(null);

  // Get available devices
  const getDevices = () => {
    if (mediaStream) {
      setMicList(mediaStream.getMicList() || []);
      setCameraList(mediaStream.getCameraList() || []);
      setActiveMicrophone(mediaStream.getActiveMicrophone());
      setActiveCamera(mediaStream.getActiveCamera());
    }
  };

  // Toggle audio device dropdown
  const toggleAudioDevices = () => {
    if (!showAudioDevices) {
      getDevices();
    }
    setShowAudioDevices(!showAudioDevices);
    setShowVideoDevices(false);
  };

  // Toggle video device dropdown
  const toggleVideoDevices = () => {
    if (!showVideoDevices) {
      getDevices();
    }
    setShowVideoDevices(!showVideoDevices);
    setShowAudioDevices(false);
  };

  // Switch microphone
  const switchMicrophone = async (deviceId) => {
    if (mediaStream && deviceId !== activeMicrophone) {
      try {
        await mediaStream.switchMicrophone(deviceId);
        setActiveMicrophone(mediaStream.getActiveMicrophone());
        setShowAudioDevices(false);
      } catch (error) {
        console.error('Failed to switch microphone:', error);
      }
    }
  };

  // Switch camera
  const switchCamera = async (deviceId) => {
    if (mediaStream && deviceId !== activeCamera) {
      try {
        await mediaStream.switchCamera(deviceId);
        setActiveCamera(mediaStream.getActiveCamera());
        setShowVideoDevices(false);
      } catch (error) {
        console.error('Failed to switch camera:', error);
      }
    }
  };

  // Close dropdowns when clicking outside
  React.useEffect(() => {
    const handleClickOutside = (event) => {
      if (audioRef.current && !audioRef.current.contains(event.target)) {
        setShowAudioDevices(false);
      }
      if (videoRef.current && !videoRef.current.contains(event.target)) {
        setShowVideoDevices(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);
  React.useEffect(() => {
    console.log(isAudioDisabled, "isAudioDisabled in ControlBar");
  }, [isAudioDisabled]);

  return (
    <div className="control-bar">
      <div className="control-container">
        <div className="controls-wrapper">
          {/* Audio Control with Device Selection */}
          <div className="control-group" ref={audioRef}>
          <div className="audio-control-wrapper">

            <button
              onClick={onToggleAudio}
              className={`control-button audio-button ${
                !mediaState.audio ? '' : 'inactive'
              }`}
              title={mediaState.audio ? 'Mute Microphone' : 'Unmute Microphone'}
              aria-label={mediaState.audio ? 'Mute Microphone' : 'Unmute Microphone'}
              // disabled={isAudioDisabled}
            >
              {mediaState.audio ? (
                <Mic className="button-icon" />
              ) : (
                <MicOff className="button-icon" />
              )}
            </button>
            </div>
            
            {/* Audio Device Dropdown Button */}
            <button
              onClick={toggleAudioDevices}
              className="control-button device-dropdown-button"
              title="Audio Devices"
              aria-label="Audio Devices"
              hidden={isAudioDisabled}

            >
              <ChevronDown className="button-icon" />
            </button>

            {/* Audio Devices Dropdown */}
            {showAudioDevices && (
              <div className="devices-dropdown">
                <div className="devices-list">
                  {micList.map((mic) => (
                    <button
                      key={mic.deviceId}
                      onClick={() => switchMicrophone(mic.deviceId)}
                      className={`device-item ${
                        mic.deviceId === activeMicrophone ? 'active' : ''
                      }`}
                    >
                      {mic.label}
                      {mic.deviceId === activeMicrophone && (
                        <span className="active-indicator">✓</span>
                      )}
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Video Control with Device Selection */}
          <div className="control-group" ref={videoRef}>
            <button
              onClick={onToggleVideo}
              className={`control-button video-button ${
                !mediaState.video ? '' : 'inactive'
              }`}
              title={mediaState.video ? 'Stop Video' : 'Start Video'}
              aria-label={mediaState.video ? 'Start Video': 'Stop Video'}
            >
              {mediaState.video ? (
                <Video className="button-icon" />
              ) : (
                <VideoOff className="button-icon" />
              )}
            </button>

            {/* Video Device Dropdown Button */}
            <button
              onClick={toggleVideoDevices}
              className="control-button device-dropdown-button"
              title="Video Devices"
              aria-label="Video Devices"
            >
              <ChevronDown className="button-icon" />
            </button>

            {/* Video Devices Dropdown */}
            {showVideoDevices && (
              <div className="devices-dropdown">
                <div className="devices-list">
                  {cameraList.map((camera) => (
                    <button
                      key={camera.deviceId}
                      onClick={() => switchCamera(camera.deviceId)}
                      className={`device-item ${
                        camera.deviceId === activeCamera ? 'active' : ''
                      }`}
                    >
                      {camera.label}
                      {camera.deviceId === activeCamera && (
                        <span className="active-indicator">✓</span>
                      )}
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Screen Share Control */}
          {canShareScreen && onToggleScreenShare && (
            <button
              onClick={onToggleScreenShare}
              className={`control-button screen-share-button ${
                !mediaState.screen ? '' : 'inactive'
              }`}
              title={mediaState.screen ? 'Stop Screen Share' : 'Share Screen'}
              aria-label={mediaState.screen ? 'Stop Screen Share' : 'Share Screen'}
            >
              {mediaState.screen ? (
                // <ScreenShare className="button-icon" />
                  <ScreenShare className="button-icon" />

              ) : (
                // <ScreenShareOff className="button-icon" />
                <ScreenShareOff className="button-icon" />

              )}
            </button>
          )}

          {/* Leave Button */}
          {canLeave && (
            <button
              onClick={onEndConsultation}
              className="control-button leave-button"
              title="Leave Consultation"
              aria-label="Leave Consultation"
            >
              <Phone className="button-icon" />
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default ControlBar;